import { getButtonLabel } from '../utils/flowButtons';
import {
  ThunderboltOutlined,
  ApiOutlined,
  BranchesOutlined,
  MessageOutlined,
  FormOutlined,
  ForkOutlined,
  StopOutlined,
  UserSwitchOutlined,
  FileTextOutlined,
  UnorderedListOutlined,
  AppstoreOutlined,
  CheckCircleOutlined,
  RobotOutlined,
} from '@ant-design/icons';

export const NODE_CATEGORIES = [
  { key: 'triggers', label: 'Triggers' },
  { key: 'messages', label: 'Messages' },
  { key: 'logic', label: 'Logic' },
  { key: 'ai', label: 'AI' },
  { key: 'integrations', label: 'Integrations' },
  { key: 'flow', label: 'Flow' },
  { key: 'handoff', label: 'Handoff' },
];

export const NODE_TYPE_REGISTRY = {
  'trigger.keyword': {
    type: 'trigger.keyword',
    label: 'Keyword',
    listLabel: (c) => `Keyword: ${(c?.keywords || []).join(', ') || '—'}`,
    subtitle: 'Starts when customer sends a keyword',
    category: 'triggers',
    color: '#722ED1',
    icon: ThunderboltOutlined,
    defaultConfig: { keywords: [], match: 'exact' },
  },
  'trigger.default': {
    type: 'trigger.default',
    label: 'Default new chat',
    listLabel: () => 'Default action (new chat)',
    subtitle: 'Runs when no keyword matches',
    category: 'triggers',
    color: '#722ED1',
    icon: ThunderboltOutlined,
    defaultConfig: {},
  },
  'trigger.regex': {
    type: 'trigger.regex',
    label: 'Regex',
    listLabel: (c) => `Regex: ${c?.pattern || '—'}`,
    subtitle: 'Match message with regular expression',
    category: 'triggers',
    color: '#722ED1',
    icon: ThunderboltOutlined,
    defaultConfig: { pattern: '' },
  },
  'message.text': {
    type: 'message.text',
    label: 'Reply message',
    listLabel: (c) => {
      const type = c?.message_type || 'text';
      if (type === 'text') {
        return `Text: ${truncate(c?.body || c?.text, 40)}`;
      }
      const typeLabel = type.charAt(0).toUpperCase() + type.slice(1);
      const name = c?.media_name || c?.media_id;
      return name ? `${typeLabel}: ${truncate(name, 36)}` : `${typeLabel} (no media)`;
    },
    subtitle: 'Send text, image, video, audio, or document',
    category: 'messages',
    color: '#52C41A',
    icon: MessageOutlined,
    defaultConfig: { body: '' },
  },
  'message.template': {
    type: 'message.template',
    label: 'Template',
    listLabel: (c) => {
      const name = c?.template_name || '—';
      const cap = c?.capture_var ? ` → ${c.capture_var}` : '';
      return `Template: ${name}${cap}`;
    },
    subtitle: 'Send WhatsApp template message',
    category: 'messages',
    color: '#52C41A',
    icon: FileTextOutlined,
    defaultConfig: { template_name: '', buttons: [], capture_var: 'last_message' },
  },
  'message.interactive_buttons': {
    type: 'message.interactive_buttons',
    label: 'Interactive buttons',
    listLabel: (c) =>
      `Buttons: ${(c?.buttons || []).map((b) => getButtonLabel(b)).join(' | ') || '—'}`,
    subtitle: 'Quick reply buttons',
    category: 'messages',
    color: '#52C41A',
    icon: AppstoreOutlined,
    defaultConfig: { body: '', buttons: [], capture_var: 'last_message' },
  },
  'message.list_dynamic': {
    type: 'message.list_dynamic',
    label: 'List from API',
    listLabel: (c) => {
      const header = c?.header || 'Dynamic list';
      const v = c?.items_var || c?.items_path || '—';
      return `List: ${header} ({{vars.${v}}})`;
    },
    subtitle: 'Build list rows from a session variable',
    category: 'messages',
    color: '#52C41A',
    icon: UnorderedListOutlined,
    defaultConfig: {
      header: '',
      body: 'Please choose an option',
      items_var: '',
      value_field: 'value',
      label_field: 'label',
      custom_rows: [],
      var_on_select: '',
      on_select_node_id: '',
      button_text: 'Select',
    },
  },
  'message.list_preset': {
    type: 'message.list_preset',
    label: 'List message (preset)',
    listLabel: (c) => `List: ${c?.preset_name || 'Select preset'}`,
    subtitle: 'Send a saved list-message preset',
    category: 'messages',
    color: '#52C41A',
    icon: UnorderedListOutlined,
    defaultConfig: {
      preset_id: null,
      preset_name: '',
      capture_var: 'last_message',
      rows: [],
    },
  },
  'logic.ask': {
    type: 'logic.ask',
    label: 'Ask question',
    listLabel: (c) => `Ask: ${c?.prompt_text || c?.var || 'input'}`,
    subtitle: 'Wait for customer reply',
    category: 'logic',
    color: '#FAAD14',
    icon: FormOutlined,
    defaultConfig: { prompt_text: '', var: 'input', invalid_reply_text: '' },
  },
  'logic.validate': {
    type: 'logic.validate',
    label: 'Validate input',
    listLabel: (c) => `Validate: ${c?.var || 'input'}`,
    subtitle: 'Check format before continuing',
    category: 'logic',
    color: '#FAAD14',
    icon: CheckCircleOutlined,
    defaultConfig: { var: 'input', validator: 'regex', pattern: '' },
  },
  'logic.condition': {
    type: 'logic.condition',
    label: 'If / Else',
    listLabel: (c) => {
      const key = c?.path || c?.key || 'condition';
      return `If / Else — ${key}`;
    },
    subtitle: 'Branch on API result or variable',
    category: 'logic',
    color: '#FA8C16',
    icon: BranchesOutlined,
    defaultConfig: {
      source: 'last_api_response',
      path: 'success',
      operator: 'equals',
      value: true,
    },
  },
  'logic.set_var': {
    type: 'logic.set_var',
    label: 'Set variable',
    listLabel: (c) => `Set: ${c?.var || '—'} = ${c?.value ?? '…'}`,
    subtitle: 'Store value in session',
    category: 'logic',
    color: '#FAAD14',
    icon: FormOutlined,
    defaultConfig: { var: 'branch', value: '' },
  },
  'logic.route_by_var': {
    type: 'logic.route_by_var',
    label: 'Route by variable',
    listLabel: (c) => `Route by: ${c?.var || 'branch'}`,
    subtitle: 'Send to different paths by var value',
    category: 'logic',
    color: '#EB2F96',
    icon: ForkOutlined,
    defaultConfig: { var: 'branch', routes: [] },
  },
  'integration.api': {
    type: 'integration.api',
    label: 'API request',
    listLabel: (c) => c?.display_name || `API: ${truncate(c?.url, 36)}`,
    subtitle: 'Call external URL',
    category: 'integrations',
    color: '#1677FF',
    icon: ApiOutlined,
    defaultConfig: {
      display_name: '',
      method: 'GET',
      url: '',
      use_token: '',
      save_from_api: {},
      headers: {},
      success_path: 'success',
      success_operator: 'equals',
      success_value: true,
    },
  },
  'flow.subflow': {
    type: 'flow.subflow',
    label: 'Open subflow',
    listLabel: (c) =>
      c?.flow_name
        ? `Subflow: ${c.flow_name}`
        : c?.flow_version_id
          ? `Subflow #${c.flow_version_id}`
          : 'Subflow: not set',
    subtitle: 'Continue in another published flow',
    category: 'flow',
    color: '#595959',
    icon: ForkOutlined,
    defaultConfig: { flow_version_id: null, flow_name: '', pass_vars: [] },
  },
  'flow.end': {
    type: 'flow.end',
    label: 'End',
    listLabel: () => 'End: Clear session',
    subtitle: 'Finish conversation step',
    category: 'flow',
    color: '#8C8C8C',
    icon: StopOutlined,
    defaultConfig: { clear_vars: [] },
  },
  'handoff.agent': {
    type: 'handoff.agent',
    label: 'Assign agent',
    listLabel: (c) => {
      if (c?.assign_mode === 'specific' && c?.agent_name) {
        return `Assign: ${c.agent_name}`;
      }
      if (c?.agent_id && c?.agent_name) {
        return `Assign: ${c.agent_name}`;
      }
      return 'Assign: auto';
    },
    subtitle: 'Pause bot and assign support',
    category: 'handoff',
    color: '#FF4D4F',
    icon: UserSwitchOutlined,
    defaultConfig: {
      assign_mode: 'auto',
      agent_id: null,
      agent_name: '',
      message: '',
      pause_chatbot: true,
    },
  },
  'ai.classify': {
    type: 'ai.classify',
    label: 'AI classify',
    listLabel: (c) => {
      const count = (c?.intents || []).length;
      return count ? `AI classify (${count} intents)` : 'AI classify';
    },
    subtitle: 'Route by AI intent',
    category: 'ai',
    color: '#7C3AED',
    icon: RobotOutlined,
    defaultConfig: {
      source_var: 'last_message',
      instructions: '',
      intents: [],
      output_var: 'intent',
      confidence_threshold: 0.7,
    },
  },
  'ai.extract': {
    type: 'ai.extract',
    label: 'AI extract',
    listLabel: (c) => {
      const count = (c?.fields || []).length;
      return count ? `AI extract (${count} fields)` : 'AI extract';
    },
    subtitle: 'Parse free text into variables',
    category: 'ai',
    color: '#7C3AED',
    icon: RobotOutlined,
    defaultConfig: {
      source_var: 'last_message',
      fields: [],
    },
  },
  'ai.reply': {
    type: 'ai.reply',
    label: 'AI reply',
    listLabel: (c) => `AI reply: ${truncate(c?.system_prompt, 32)}`,
    subtitle: 'AI agent answers the customer',
    category: 'ai',
    color: '#7C3AED',
    icon: RobotOutlined,
    defaultConfig: {
      system_prompt: '',
      knowledge_var: '',
      knowledge_text: '',
      history_turns: 6,
      max_tokens: 1024,
      handoff_on_uncertain: false,
      end_keywords: [],
    },
  },
  'ai.generate': {
    type: 'ai.generate',
    label: 'AI generate',
    listLabel: (c) =>
      c?.send_inline ? 'AI generate (send message)' : `AI generate → ${c?.output_var || 'var'}`,
    subtitle: 'Generate text into a variable or send inline',
    category: 'ai',
    color: '#7C3AED',
    icon: RobotOutlined,
    defaultConfig: {
      prompt: '',
      output_var: '',
      send_inline: false,
      temperature: 0.7,
    },
  },
};

export const NODE_TYPE_LIST = Object.values(NODE_TYPE_REGISTRY);

export const getNodeMeta = (type) =>
  NODE_TYPE_REGISTRY[type] || {
    type,
    label: type,
    listLabel: () => type,
    subtitle: '',
    category: 'logic',
    color: '#8C8C8C',
    icon: MessageOutlined,
    defaultConfig: {},
  };

export const createNode = (type, id, position = { x: 0, y: 0 }) => {
  const meta = getNodeMeta(type);
  return {
    id: id || `node-${Date.now()}`,
    type,
    config: JSON.parse(JSON.stringify(meta.defaultConfig || {})),
    position,
  };
};

function truncate(str, len) {
  if (!str) return '—';
  const s = String(str);
  return s.length > len ? `${s.slice(0, len)}…` : s;
}
